import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Shield, ArrowLeft, KeyRound } from "lucide-react";
import Logo from "@/components/site/Logo";

export default function AdminSetup() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [expectedEmail, setExpectedEmail] = useState("");
    const [needsSetup, setNeedsSetup] = useState(null);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/auth/setup-status");
                setNeedsSetup(data.needs_setup);
                setExpectedEmail(data.expected_email);
                if (!data.needs_setup) {
                    // Setup already done — bounce to login
                    setTimeout(() => navigate("/admin/login", { replace: true }), 2000);
                }
            } catch (err) {
                toast.error("Could not reach the server. Please try again.");
            } finally {
                setChecking(false);
            }
        })();
    }, [navigate]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords do not match");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/setup", { email: expectedEmail, password });
            toast.success("Admin account created. Signing you in...");
            await login(expectedEmail, password);
            navigate("/admin/dashboard", { replace: true });
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) || "Setup failed"
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (checking) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                    Checking setup status...
                </div>
            </div>
        );
    }

    if (needsSetup === false) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6">
                <div className="max-w-md text-center">
                    <Shield className="w-10 h-10 mx-auto text-[#d52b1e] mb-4" />
                    <div className="font-display uppercase text-3xl mb-2">Setup Complete</div>
                    <p className="text-zinc-400 mb-6 text-sm">
                        An admin account already exists. This setup page is now disabled.
                    </p>
                    <Link
                        to="/admin/login"
                        className="inline-block bg-[#d52b1e] hover:bg-[#f03022] text-white px-6 py-3 uppercase text-xs tracking-[0.25em] font-bold"
                        data-testid="setup-goto-login"
                    >
                        Go to login
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white grid lg:grid-cols-2">
            <div className="hidden lg:block relative bg-[#18181a] border-r border-white/10">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{
                        backgroundImage:
                            "url(https://images.pexels.com/photos/33814734/pexels-photo-33814734.jpeg?auto=compress&cs=tinysrgb&w=1200)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-black/70 to-black/90" />
                <div className="absolute top-0 left-0 w-full h-2 hazard-stripes opacity-80" />
                <div className="relative z-10 h-full flex flex-col justify-between p-12">
                    <Logo variant="compact" />
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#d52b1e] mb-2">
                            First-time Setup
                        </div>
                        <h1 className="font-display uppercase text-5xl leading-none">
                            Create your<br />admin account.
                        </h1>
                        <p className="text-zinc-400 mt-4 max-w-sm">
                            This page is only available once. After you set your password it
                            will disable itself automatically.
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center p-8">
                <form
                    onSubmit={submit}
                    className="w-full max-w-md bg-[#18181a] border border-white/10 p-8"
                    data-testid="admin-setup-form"
                >
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-white mb-8"
                    >
                        <ArrowLeft className="w-3 h-3" /> Back to site
                    </Link>
                    <div className="flex items-center gap-3 mb-8">
                        <div className="bg-[#d52b1e] text-white w-10 h-10 flex items-center justify-center">
                            <KeyRound className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                                Set your password
                            </div>
                            <div className="font-display uppercase text-2xl">One-time setup</div>
                        </div>
                    </div>

                    <div className="mb-6 p-4 border border-white/10 bg-black/30">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                            Admin email (fixed)
                        </div>
                        <div className="text-white text-sm" data-testid="setup-email-value">
                            {expectedEmail || "not configured"}
                        </div>
                    </div>

                    <div className="space-y-7">
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Choose a password (min. 8 chars)"
                            className="industrial-input"
                            required
                            minLength={8}
                            autoComplete="new-password"
                            data-testid="setup-password-input"
                        />
                        <input
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            placeholder="Confirm password"
                            className="industrial-input"
                            required
                            minLength={8}
                            autoComplete="new-password"
                            data-testid="setup-confirm-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="mt-8 w-full bg-[#d52b1e] hover:bg-[#f03022] disabled:opacity-60 text-white px-5 py-3 uppercase text-xs tracking-[0.25em] font-bold transition-colors"
                        data-testid="setup-submit-btn"
                    >
                        {submitting ? "Creating..." : "Create admin account"}
                    </button>
                </form>
            </div>
        </div>
    );
}
